// ============================================================
// Motor de cálculo de ruido ocupacional — NTP 638 (INSHT, 2003)
// "Exposición laboral al ruido: evaluación del riesgo, importancia de
// las bandas de frecuencia" — método octava a octava para seleccionar
// protectores auditivos y calcular el nivel efectivo bajo protector.
// Marco normativo de referencia: Anexo 3 AM 196 — Norma Técnica en
// Seguridad e Higiene del Trabajo (ver docs/normativa/anexo3_seguridad_higiene.md).
// Nivel de pico (ruido impulsivo): Guía Técnica INSST 2022 "Evaluación y
// prevención de los riesgos relacionados con la exposición al ruido en los
// lugares de trabajo", Apéndice 4 §4.2.
//
// Alcance de esta primera fase (motor de cálculo puro, sin UI todavía):
//   - Estadística de lecturas por banda de octava (mf, σ)
//   - APVf por banda (Tabla 1 NTP 638)
//   - Nivel total ponderado A, con y sin protector (suma logarítmica)
//   - PNR por método HML (valores H/M/L de fabricante) y por método SNR
//   - Nivel de pico atenuado para ruido impulsivo (LCpico'), Guía INSST
//     2022 Apéndice 4 §4.2, con límites de la Directiva 2003/10/CE, Art. 3
//   - LAeq diario (jornadas parciales / tiempo real de uso del protector)
//   - Verificación de cumplimiento contra el límite de 8h de AM 196
//
// NO implementado en esta fase (ver TODO explícito más abajo):
//   - "Método de comprobación HML" (4º método mencionado por INSST) — su
//     fórmula exacta no está confirmada en el NTP 638 disponible ni en
//     ninguna fuente verificada. Pendiente de que Kevin aporte la
//     referencia normativa exacta antes de implementarlo.
// ============================================================

// ===== Bandas de octava y ponderación A =====

export const BANDAS_OCTAVA = [63, 125, 250, 500, 1000, 2000, 4000, 8000] as const;
export type FrecuenciaBanda = (typeof BANDAS_OCTAVA)[number];

// Valores estándar de ponderación A por banda de octava, usados por el NTP
// 638 para pasar de nivel de presión sonora (dB, lineal/Z) a nivel ponderado
// A (dBA). Verificados contra el ejemplo de la Tabla 2-5 del NTP 638 (ver
// bloque de verificación al final de este archivo): con estos valores el
// motor reproduce LA≈93 dBA / LA'≈68 dBA / PNR84≈25 dB del ejemplo oficial.
export const PONDERACION_A: Record<FrecuenciaBanda, number> = {
  63: -26.2,
  125: -16.1,
  250: -8.6,
  500: -3.2,
  1000: 0,
  2000: 1.2,
  4000: 1.0,
  8000: -1.1,
};

// NTP 638, Tabla 6 — ponderación C por banda de octava, usada para pasar de
// nivel de presión sonora (dB, lineal/Z) a nivel ponderado C (dBC). A
// diferencia de la ponderación A, es casi plana en el rango medio (0 dB de
// 250 a 1000 Hz) y solo atenúa en los extremos. Verificada contra el mismo
// ejemplo de la Tabla 2-5 del NTP 638 (Lf = [85,85,87,90,90,85,82,78]): con
// estos valores calcularNivelTotalPonderadoC() reproduce LC=95 dBC, el
// resultado que da el propio NTP 638 en su Tabla 6.
export const PONDERACION_C: Record<FrecuenciaBanda, number> = {
  63: -0.8,
  125: -0.2,
  250: 0,
  500: 0,
  1000: 0,
  2000: -0.2,
  4000: -0.8,
  8000: -3,
};

// ===== 1. Estadística de lecturas por banda de octava =====

export interface EstadisticaBandas {
  bandas: readonly number[]; // frecuencias, mismo orden que BANDAS_OCTAVA
  mf: number[];               // "valor más probable" (1 lectura) o promedio (2+) por banda
  sigma: number[];             // desviación estándar por banda (0 si solo hay 1 lectura)
}

// NTP 638: cuando se dispone de una sola lectura por banda, esta se usa
// directamente como valor más probable (σ = 0, no hay variabilidad que
// estimar). Con 2+ lecturas se calcula el promedio (mf) y la desviación
// estándar (σ) por banda, insumos de calcularAPVf().
//
// USO ESPERADO DEL MÓDULO: en el caso normal, mf y σ NO se calculan aquí —
// vienen directamente de la ficha técnica del fabricante del protector
// auditivo (folleto con la atenuación media y su desviación estándar por
// banda, como en el ejemplo del NTP 638 Tabla 3), y se pasan tal cual a
// calcularAPVf(). Esta función queda como utilidad OPCIONAL para el caso
// menos común de que alguien tenga múltiples ensayos propios de laboratorio
// y necesite derivar mf/σ a partir de esas lecturas repetidas.
export function calcularEstadisticaBandas(lecturas: number[][]): EstadisticaBandas {
  if (lecturas.length === 0) {
    throw new Error('calcularEstadisticaBandas requiere al menos una lectura');
  }
  const nBandas = BANDAS_OCTAVA.length;
  lecturas.forEach((lectura, i) => {
    if (lectura.length !== nBandas) {
      throw new Error(
        `La lectura #${i + 1} debe tener ${nBandas} valores (uno por banda de octava 63-8000 Hz), recibió ${lectura.length}`
      );
    }
  });

  const mf: number[] = [];
  const sigma: number[] = [];

  for (let banda = 0; banda < nBandas; banda++) {
    const valores = lecturas.map((l) => l[banda]);
    const promedio = valores.reduce((a, b) => a + b, 0) / valores.length;
    mf.push(promedio);

    if (valores.length === 1) {
      sigma.push(0);
    } else {
      // Desviación estándar MUESTRAL (n-1, corrección de Bessel): correcta
      // para el caso de uso de esta función — estimar la variabilidad real
      // del ruido a partir de una muestra de ensayos propios repetidos.
      // (No se verifica contra el Excel de Kevin: ese ejercicio calcula la
      // precisión de una medición ambiental repetida —promedio simple de
      // los 8 promedios por banda, sin relación con desviación estándar—,
      // un concepto distinto al de esta función. Ver nota de uso esperado
      // más arriba: en el caso normal, σ viene de la ficha del fabricante,
      // no se calcula aquí.)
      const varianza =
        valores.reduce((acc, v) => acc + Math.pow(v - promedio, 2), 0) / (valores.length - 1);
      sigma.push(Math.sqrt(varianza));
    }
  }

  return { bandas: BANDAS_OCTAVA, mf, sigma };
}

// ===== 2. APVf (Assumed Protection Value por banda de frecuencia) =====

export type EficaciaProteccion = 75 | 80 | 84 | 85 | 90 | 95 | 99.5;

// NTP 638, Tabla 1 — valor de z según el percentil de eficacia deseado de la
// protección auditiva (a mayor eficacia exigida, mayor z, más conservador).
// NOTA: la Guía Técnica INSST 2022 (Apéndice 4, Tabla 3) trae una tabla de
// eficacias parcialmente distinta (84%→1,00, 90%→1,28, 95%→1,64, 98%→2,00 —
// sin 75/80/85/99,5% y con 98% en vez de 99,5%). Ambas fuentes coinciden en
// el valor por defecto usado aquí (84%→1,00), que es lo único que importa
// para el motor hoy; no hace falta reconciliar las dos tablas completas por
// ahora.
const Z_POR_EFICACIA: Record<EficaciaProteccion, number> = {
  75: 0.67,
  80: 0.84,
  84: 1.0,
  85: 1.04,
  90: 1.28,
  95: 1.64,
  99.5: 2.58,
};

// NTP 638: APVf = mf - z·σ. Default 84% — eficacia estándar de la industria
// salvo que se indique explícitamente lo contrario.
export function calcularAPVf(
  mf: number,
  sigma: number,
  eficacia: EficaciaProteccion = 84
): number {
  const z = Z_POR_EFICACIA[eficacia];
  return mf - z * sigma;
}

// ===== 3. Nivel total ponderado A (suma logarítmica por banda) =====

// NTP 638: LA = 10·log10(Σ 10^(0.1·(Lf + Af))) sin protector; con protector
// (conProtector=true) se resta el APVf de cada banda antes de sumar:
// LA' = 10·log10(Σ 10^(0.1·(Lf + Af - APVf))). El resultado se redondea al
// entero más próximo (regla explícita del NTP).
export function calcularNivelTotalPonderadoA(
  lecturasPorBanda: number[],
  conProtector: boolean,
  apvfPorBanda?: number[]
): number {
  if (lecturasPorBanda.length !== BANDAS_OCTAVA.length) {
    throw new Error(`lecturasPorBanda debe tener ${BANDAS_OCTAVA.length} valores (63-8000 Hz)`);
  }
  if (conProtector && (!apvfPorBanda || apvfPorBanda.length !== BANDAS_OCTAVA.length)) {
    throw new Error('conProtector=true requiere apvfPorBanda con un valor por banda de octava');
  }

  const suma = BANDAS_OCTAVA.reduce((acc, freq, i) => {
    const Lf = lecturasPorBanda[i];
    const Af = PONDERACION_A[freq];
    const APVf = conProtector ? (apvfPorBanda as number[])[i] : 0;
    return acc + Math.pow(10, 0.1 * (Lf + Af - APVf));
  }, 0);

  return Math.round(10 * Math.log10(suma));
}

// ===== 3b. Nivel total ponderado C (suma logarítmica por banda) =====

// NTP 638, Tabla 6: LC = 10·log10(Σ 10^(0.1·(Lf + Cf))), mismo patrón que
// calcularNivelTotalPonderadoA pero con la ponderación C (PONDERACION_C) en
// vez de la A. Sirve para obtener el LC global que requieren los métodos
// HML y SNR (calcularPNR_HML, calcularPNR_SNR) a partir de una medición por
// bandas de octava, cuando no se dispone de una medición LC directa del
// sonómetro. El resultado se redondea al entero más próximo (mismo criterio
// que el NTP aplica a LA).
export function calcularNivelTotalPonderadoC(lecturasPorBanda: number[]): number {
  if (lecturasPorBanda.length !== BANDAS_OCTAVA.length) {
    throw new Error(`lecturasPorBanda debe tener ${BANDAS_OCTAVA.length} valores (63-8000 Hz)`);
  }

  const suma = BANDAS_OCTAVA.reduce((acc, freq, i) => {
    const Lf = lecturasPorBanda[i];
    const Cf = PONDERACION_C[freq];
    return acc + Math.pow(10, 0.1 * (Lf + Cf));
  }, 0);

  return Math.round(10 * Math.log10(suma));
}

// ===== 4. PNR — método HML (valores H/M/L de fabricante) =====

// NTP 638: Predicted Noise Reduction a partir de los valores H (alta
// frecuencia), M (media) y L (baja) que declara el fabricante del
// protector, y de LC/LA medidos en el puesto de trabajo (con sonómetro en
// C y en A respectivamente).
export function calcularPNR_HML(H: number, M: number, L: number, LC: number, LA: number): number {
  const diferencia = LC - LA;
  if (diferencia <= 2) {
    return M - ((H - M) / 4) * (diferencia - 2);
  }
  return M - ((M - L) / 8) * (diferencia - 2);
}

// TODO(Kevin): "Método de comprobación HML" (4º método mencionado por
// INSST, para verificar la consistencia de los valores H/M/L del
// fabricante contra una medición octava a octava). NO implementado — su
// fórmula exacta no está confirmada en el NTP 638 que tenemos ni en
// ninguna fuente verificada. No inventar la fórmula: pendiente de que
// Kevin aporte la referencia normativa exacta.

// ===== 5. PNR — método SNR (Single Number Rating) =====

// NTP 638: método simplificado cuando el fabricante solo declara un SNR
// único (en vez de H/M/L). LA' = LC - SNR.
export function calcularPNR_SNR(LC: number, SNR: number): number {
  return LC - SNR;
}

// ===== 5b. Nivel de pico atenuado (ruido impulsivo) =====

export type FrecuenciaDominanteImpulso = 'bajas' | 'medias_altas' | 'altas';

// Guía Técnica INSST 2022, Apéndice 4, §4.2 "Método destinado a determinar
// la atenuación de un protector auditivo para el nivel de pico de ruido
// impulsivo": LCpico' = LCpico - X, donde X sale de la Tabla 13 según la
// frecuencia dominante del impulso (clasificada con la Tabla 12 — ver nota
// más abajo). Reutiliza los mismos H/M/L del protector ya usados en
// calcularPNR_HML; no requiere ningún dato adicional del protector.
//
// Aplicable a: protectores pasivos, protectores no pasivos dependientes del
// nivel, y protectores con reducción activa del ruido (según el texto).
//
// La Tabla 12 (clasificación de fuentes de ruido impulsivo típicas por
// frecuencia dominante — p.ej. explosivos/martillo → bajas, clavadora →
// medias-altas, pistola → altas) NO se hardcodea aquí: su extracción del
// PDF no es confiable (columnas mezcladas en la conversión), y además es un
// dato que el usuario debe seleccionar directamente en el formulario
// (Fase 2) según la fuente real de su puesto de trabajo, no una lista
// cerrada que el motor deba imponer.
export function calcularAtenuacionNivelPico(
  LCpico: number,
  frecuenciaDominante: FrecuenciaDominanteImpulso,
  H: number,
  M: number,
  L: number
): number {
  const X =
    frecuenciaDominante === 'bajas' ? L - 5 :
    frecuenciaDominante === 'medias_altas' ? M - 5 :
    H;
  return LCpico - X;
}

// TODO(Kevin): "Método de comprobación HML" (4º método mencionado por
// INSST, para verificar la consistencia de los valores H/M/L del
// fabricante contra una medición octava a octava). NO implementado — su
// fórmula exacta no está confirmada en el NTP 638 que tenemos ni en
// ninguna fuente verificada. No inventar la fórmula: pendiente de que
// Kevin aporte la referencia normativa exacta. (Distinto del nivel de pico
// LCpico de arriba, que ya está implementado y verificado.)

// ===== 6. LAeq diario (jornada parcial / tiempo real de uso del protector) =====

export interface PeriodoExposicion {
  nivelDb: number; // nivel (LAeq o LA' del período, según corresponda)
  horas: number;   // horas de exposición a ese nivel dentro de la jornada
}

// NTP 638: LAeqT = 10·log10((1/T)·Σ(Ti·10^(0.1·LAeqi))), donde T es la
// jornada de referencia (8h por defecto) y Ti/LAeqi son las horas y el
// nivel de cada período. Cubre tanto la exposición parcial a un único nivel
// medido (ej. 3.5h a nivel medido + 4.5h de ruido despreciable — el período
// de ruido despreciable puede omitirse directamente si su nivel es tan bajo
// que su aporte a la suma logarítmica es insignificante) como la Tabla 9 del
// NTP (tiempo real de uso del protector vs. tiempo sin protector dentro de
// la jornada, pasando el nivel LA' con protector y el nivel LA sin protector
// como períodos separados).
export function calcularLAeqDiario(
  periodos: PeriodoExposicion[],
  jornadaTotal: number = 8
): number {
  if (jornadaTotal <= 0) return 0;
  const sumaPonderada = periodos.reduce(
    (acc, p) => acc + p.horas * Math.pow(10, 0.1 * p.nivelDb),
    0
  );
  return 10 * Math.log10(sumaPonderada / jornadaTotal);
}

// ===== 7. Cumplimiento AM 196 =====

export interface CumplimientoRuidoAM196 {
  cumple: boolean;
  limite: number;
  margen: number; // límite - laeqDiario: positivo = margen disponible, negativo = excede
}

// Límite de 85 dB(A)/8h — CONFIRMADO contra fuente primaria oficial:
// Anexo 3 — Norma Técnica en Seguridad e Higiene del Trabajo, Art. 4 (Ruido),
// numeral 5 "Valores límite de exposición" (trabajo.gob.ec):
// "La exposición ocupacional a ruido continuo deberá ser controlada de modo
// que ningún trabajador pueda estar expuesto por 8 horas o más, a un Nivel
// de Presión Sonora Continuo Equivalente superior a 85 dB(A), medidos en la
// posición del oído del trabajador."
// Cadena legal: DE 255 (2024) Art. 48 remite los límites de exposición a la
// normativa técnica nacional vigente, que es el Anexo 3 del AM 196 (2024).
// El DE 2393 NO está vigente: la Disposición Transitoria Décima Segunda del
// DE 255 lo mantuvo solo hasta que el MDT emitiera esa normativa
// (verificado el 28/09/2026; DE 255 Art. 48 leído en el texto oficial).
// Confirmado 2026-09-16, cita literal verificada contra el PDF oficial.
export const LIMITE_AM196_8H = 85;

export function verificarCumplimientoAM196(laeqDiario: number): CumplimientoRuidoAM196 {
  return {
    cumple: laeqDiario <= LIMITE_AM196_8H,
    limite: LIMITE_AM196_8H,
    margen: LIMITE_AM196_8H - laeqDiario,
  };
}

// ===== 7b. Límites de referencia para nivel de pico =====

export interface LimitePicoResult {
  cumpleLimite: boolean;      // lcPicoAtenuado <= límite máximo
  requiereAccionSuperior: boolean; // lcPicoSinProtector >= valor superior de acción
  requiereAccionInferior: boolean; // lcPicoSinProtector >= valor inferior de acción
  lcPicoSinProtector: number;      // pico medido, sin protector (base de las acciones)
  limite: number;
  accionSuperior: number;
  accionInferior: number;
}

// Nivel de pico (LCpico): Directiva 2003/10/CE, Art. 3 — valor límite de
// exposición 140 dB(C) (200 Pa), valor superior de acción 137 dB(C) (140 Pa)
// y valor inferior de acción 135 dB(C) (112 Pa). Se aplica por remisión del
// DE 255 Art. 48 ("en caso de ausencia, se deberá referir a normas
// internacionales reconocidas"): el Anexo 3 del AM 196 no fija límite de
// pico (su Art. 4, num. 3, lit. i solo pide considerar "toda exposición a
// ruido de impulsos" al medir). El RD 286/2006 de España, citado antes aquí,
// es la trasposición de esta misma directiva, con los mismos valores.
// Vigencia verificada el 28/09/2026: Directiva 2003/10/CE en vigor (versión
// consolidada EUR-Lex 2019).
//
// Art. 3.2 de la directiva: el VALOR LÍMITE se evalúa teniendo en cuenta la
// atenuación del protector (→ lcPicoAtenuado); los VALORES DE ACCIÓN no la
// tienen en cuenta (→ lcPicoSinProtector, el pico medido).
export const LIMITE_PICO_DIR_2003_10 = 140;          // dB(C) — valor límite de exposición
export const ACCION_SUPERIOR_PICO_DIR_2003_10 = 137; // dB(C) — valor superior de acción
export const ACCION_INFERIOR_PICO_DIR_2003_10 = 135; // dB(C) — valor inferior de acción

export function verificarLimitePico(lcPicoAtenuado: number, lcPicoSinProtector: number): LimitePicoResult {
  return {
    cumpleLimite: lcPicoAtenuado <= LIMITE_PICO_DIR_2003_10,
    requiereAccionSuperior: lcPicoSinProtector >= ACCION_SUPERIOR_PICO_DIR_2003_10,
    requiereAccionInferior: lcPicoSinProtector >= ACCION_INFERIOR_PICO_DIR_2003_10,
    lcPicoSinProtector,
    limite: LIMITE_PICO_DIR_2003_10,
    accionSuperior: ACCION_SUPERIOR_PICO_DIR_2003_10,
    accionInferior: ACCION_INFERIOR_PICO_DIR_2003_10,
  };
}

// Texto único del valor de acción alcanzado (pantalla y PDF); null si no
// alcanza ninguno. El pico va con un decimal para que el número mostrado no
// contradiga la acción (136,6 no debe leerse "137" con acción inferior).
// En el PDF se evita "≥" (Helvetica de jsPDF no lo tiene).
export function textoAccionPico(r: LimitePicoResult, paraPdf = false): string | null {
  const accion = r.requiereAccionSuperior ? { valor: r.accionSuperior, nombre: 'superior' }
    : r.requiereAccionInferior ? { valor: r.accionInferior, nombre: 'inferior' } : null;
  if (!accion) return null;
  const cmp = paraPdf ? `, igual o superior a ${accion.valor} dB(C)` : ` ≥ ${accion.valor} dB(C)`;
  return `LCpico medido sin protector ${r.lcPicoSinProtector.toFixed(1)} dB(C)${cmp}: valor ${accion.nombre} de acción ` +
    '(Directiva 2003/10/CE, Art. 3.2: los valores de acción no consideran el protector).';
}

// ============================================================
// Verificación manual contra el NTP 638, Tabla 2-5 (ejemplo oficial)
// ============================================================
// Datos de entrada (bandas 63-8000 Hz, mismo orden que BANDAS_OCTAVA):
//   Lf    = [85, 85, 87, 90, 90, 85, 82, 78]   (nivel medido por banda, dB)
//   mf    = [24.9, 25.4, 25.9, 27.8, 28.3, 33.2, 30.9, 40.2]  (atenuación media del protector, dB)
//   sigma = [6.4, 6.1, 3.8, 2.5, 3.4, 4.9, 5.2, 4.9]           (desv. estándar de la atenuación, dB)
//
// const APVf = mf.map((m, i) => calcularAPVf(m, sigma[i], 84));
// // APVf ≈ [18.5, 19.3, 22.1, 25.3, 24.9, 28.3, 25.7, 35.3]
//
// const LA  = calcularNivelTotalPonderadoA(Lf, false);            // → 93 dBA
// const LAp = calcularNivelTotalPonderadoA(Lf, true, APVf);       // → 68 dBA
// const PNR84 = LA - LAp;                                          // → 25 dB
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   LA ≈ 93 dBA, LA' ≈ 68 dBA, PNR84 ≈ 25 dB — coincide con el ejemplo del
//   NTP 638. Pendiente: correr esto mismo como snippet real (consola /
//   test) una vez se decida si el proyecto quiere tests automatizados para
//   motores de cálculo (hoy iluminacion.ts y coshhEssentials.ts tampoco
//   los tienen — se verifican a mano igual que aquí).
// ============================================================

// ============================================================
// Verificación manual contra la Guía Técnica INSST 2022, Apéndice 4 §4.2
// (ejemplo oficial: golpes de martillo, frecuencia medias-altas)
// ============================================================
// const resultado = calcularAtenuacionNivelPico(139, 'medias_altas', H, 25, L);
// // X = M - 5 = 25 - 5 = 20 → resultado = 139 - 20 = 119
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   LCpico' = 119 dB(C) — coincide exactamente con el ejemplo del documento
//   ("139 - 25 + 5 = 119").
// ============================================================

// ============================================================
// Verificación manual contra el NTP 638, Tabla 6 (ejemplo oficial de LC)
// ============================================================
// Mismo Lf de la Tabla 2-5: [85, 85, 87, 90, 90, 85, 82, 78]
//
// const LC = calcularNivelTotalPonderadoC([85, 85, 87, 90, 90, 85, 82, 78]);
//
// Resultado esperado (confirmado a mano antes de guardar este archivo):
//   LC = 95 dBC — coincide exactamente con el resultado que da el propio
//   NTP 638 en su Tabla 6 para este mismo ejemplo.
// ============================================================
